import { Const } from '~/constants/Const.constant';
import { mapUsersDetails } from '~/helpers';
import operations from '~/helpers/device-operations';
import { lastData, shareWeathermap, stationName, weatherMapFilteringReason, weatherMapIsFiltered } from '~/helpers/fields-description';
import { rfStatus } from '~/helpers/network-fields-description';
import { BehaviorKey, Operation } from '~/interfaces/device.interface';
import { FieldObject } from '~/interfaces/field.interface';
import { Gateway } from '~/models/gateways/gateway.model';
import { getServerNodeFields } from '~/models/helpers/network.function';
import apiService from '~/services/api.service';
import { HomesModule } from '~/store/modules/homes';

export class NAMain extends Gateway {
    MAX_TIME_BEFORE_CONSIDERED_DISCONNECTED = 10 * 60;

    hasOperationMainloop = false;
    behaviorFields: BehaviorKey[] = [
        'led_disabled',
        'rf_statistics_enabled',
        'touch_disabled',
        'usb_host_disabled',
        'legacy_offset',
    ];
    weathermap_marks_data?: {
        is_filtered: boolean;
        reason: string;
    };

    rf_levels: { [key: string]: number } = {
        full: Const.NARadioRssiTreshold.RADIO_THRESHOLD_3,
        high: Const.NARadioRssiTreshold.RADIO_THRESHOLD_2,
        medium: Const.NARadioRssiTreshold.RADIO_THRESHOLD_1,
        low: Const.NARadioRssiTreshold.RADIO_THRESHOLD_0,
    };

    loadData(data: any) {
        data.name = data.name || data.module_name;
        super.loadData(data);
        this.loadOffset(data.offset);
    }

    get fieldStationName() {
        return stationName(this.name);
    }
    get fieldShareWeathermap() {
        return shareWeathermap(this.public_ext_data);
    }
    get fieldLastData() {
        return lastData(this.last_data, this.outdatedLastData ? 'error--text' : '');
    }
    get fieldWeatherMapIsFiltered() {
        return weatherMapIsFiltered(this.weathermap_marks_data?.is_filtered);
    }
    get fieldWeatherMapFilteringReason() {
        if (this.weathermap_marks_data?.is_filtered) {
            return weatherMapFilteringReason(this.weathermap_marks_data?.reason);
        }
    }
    get fieldSubtype() {
        return {
            key: 'subtype',
            value: this.subtype === 'color-weather-station' ? 'Color': 'Grey',
            type: 'string',
        };
    }

    get deviceOperations(): Operation[]  {
        const deviceOperations = super.deviceOperations.concat([
            operations.dump({ callback: this.dump }),
            operations.dumpLogs({ callback: this.dumpLogs }),
            operations.logs({ callback: this.logs }),
            operations.orderReboot({ callback: this.orderReboot, disabled: this.isOrderRebootDisabled }),
            operations.setDump({ callback: this.setDump }),
            operations.deleteCache({ callback: this.deleteCache }),
            operations.recalibrateCO2({ callback: this.recalibrateCO2 }),
        ]);
        if (this.canMoveToTemporaryHome) {
            deviceOperations.push(operations.moveToTemporaryHome({ callback: this.moveToTemporaryHome }));
        }
        return deviceOperations;
    }

    getNetworkFields(): FieldObject[] {
        let fields: FieldObject[] = super.getNetworkFields();
        if (this.network) {
            fields = fields.concat([
                rfStatus(this.getRfIcon(this.network.rf_amb_status), this.network.rf_amb_status),
            ]);
            if (this.network.server_node) {
                fields = fields.concat(getServerNodeFields(this.network.server_node));
            }
        }
        return fields;
    }

    // a station can be moved if it is in a home, but not a temporary one (checked by looking at whether the home has a
    // name or not)
    get canMoveToTemporaryHome() {
        return HomesModule.currentHome?.name;
    }

    moveToTemporaryHome = async () => {
        this.handleOperation({
            callback: () => apiService.movedevicetonewtemporaryhome({ device_id: this.id }),
            confirmText: 'You are about to move the device to a temporary home, do you want to continue?',
        });
    }

    get friendUsers() {
        return this.friend_users_details ? mapUsersDetails(this.friend_users_details) : [];
    }

    get canBeConsideredDisconnected() {
        // station v1 and v2 are considered disconnected when disconnection time after 10 minutes
        // other hardware versions are considered disconnected if there is a disconnection time
        return this.hw_version && [250, 251].includes(this.hw_version)
            ? super.canBeConsideredDisconnected
            : true;
    }

    getRfIcon(status?: number) {
        for (const threshold in this.rf_levels) {
            if (status && status < this.rf_levels[threshold]) {
                return `signal_${threshold}`;
            }
        }
        if (!status) {
            return 'signal_unknown';
        }
        return 'signal_low';
    }

}
